import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { errMsg, money, API } from "../../lib/api";
import { useAdmin, H, Table, StatusBadge } from "./AdminLayout";
import { BookingDetail, bookingCols } from "./AdminBookings";

const Stat = ({ label, value, testid }) => <div className="card !p-5"><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 text-2xl font-bold text-slate-900" data-testid={testid}>{value}</p></div>;

export function Overview() {
  const { call } = useAdmin();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { call("get", "/admin/overview").then(setD).catch((e) => toast.error(errMsg(e))); }, [call]);
  if (!d) return null;
  return (
    <>
      <H title="Business overview" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today's jobs" value={d.today_count} testid="overview-today" />
        <Stat label="Confirmed upcoming" value={d.counts.confirmed + d.counts.assigned} testid="overview-confirmed" />
        <Stat label="Awaiting payment" value={d.counts.pending_payment} />
        <Stat label="New quote requests" value={d.new_quotes} testid="overview-new-quotes" />
        {d.revenue_month !== undefined && <Stat label="Revenue this month" value={money(d.revenue_month)} testid="overview-revenue" />}
        {d.outstanding !== undefined && <Stat label="Outstanding balances" value={money(d.outstanding)} />}
        <Stat label="Customers" value={d.customers} />
        <Stat label="Needs scheduling" value={d.counts.needs_scheduling} />
      </div>
      <h2 className="text-lg font-semibold">Next 7 days</h2>
      <Table testid="overview-upcoming" cols={bookingCols} rows={d.upcoming} onRow={(b) => setOpen(b.id)} />
      <BookingDetail id={open} onClose={() => setOpen(null)} />
    </>
  );
}

export function Customers() {
  const { call } = useAdmin();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => call("get", `/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(setRows);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const show = async (r) => setC(await call("get", `/admin/customers/${r.id}`));
  const saveNotes = async () => { await call("patch", `/admin/customers/${c.id}`, { notes: c.notes, name: c.name, phone: c.phone }); toast.success("Customer saved"); load(); };
  return (
    <>
      <H title="Customers"><input className="field !py-2 w-56" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="customers-search-input" /></H>
      <Table testid="customers-table" rows={rows} onRow={show} cols={[["Name", (r) => r.name], ["Email", (r) => r.email], ["Phone", (r) => r.phone], ["Bookings", (r) => r.booking_count], ["Since", (r) => r.created_at.slice(0, 10)]]} />
      <Dialog open={!!c} onOpenChange={(o) => !o && setC(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          {c && <>
            <DialogHeader><DialogTitle>{c.name}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <input className="field" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} />
              <input className="field" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} />
              <p className="sm:col-span-2">{c.email} · {c.addresses.map((a) => `${a.line1}, ${a.city} ${a.zip}`).join(" | ")}</p>
              <textarea className="field sm:col-span-2" rows={2} placeholder="Internal notes" value={c.notes || ""} onChange={(e) => setC({ ...c, notes: e.target.value })} data-testid="customer-notes-input" />
              <button className="btn-dark w-fit" onClick={saveNotes} data-testid="customer-save-button">Save</button>
            </div>
            <h3 className="font-semibold mt-3">Booking history</h3>
            <Table testid="customer-bookings" cols={bookingCols} rows={c.bookings} onRow={(b) => setOpen(b.id)} />
            <h3 className="font-semibold mt-3">Payment history</h3>
            <Table testid="customer-payments" rows={c.payments.filter((p) => p.status !== "initiated")} cols={[["Date", (p) => p.created_at.slice(0, 10)], ["Kind", (p) => p.kind], ["Amount", (p) => money(p.amount)], ["Refunded", (p) => money(p.refunded_amount)], ["Status", (p) => <StatusBadge s={p.status} />]]} />
          </>}
        </DialogContent>
      </Dialog>
      <BookingDetail id={open} onClose={() => setOpen(null)} />
    </>
  );
}

function Photo({ id }) {
  const { call } = useAdmin();
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url;
    call("get", `/admin/files/${id}`, undefined, { responseType: "blob" }).then((b) => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, call]);
  return src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Customer upload" className="h-28 w-full object-cover rounded-lg" /></a> : <div className="h-28 bg-slate-100 rounded-lg animate-pulse" />;
}

export function Quotes() {
  const { call } = useAdmin();
  const [rows, setRows] = useState([]);
  const [r, setR] = useState(null);
  const [q, setQ] = useState({ amount: "", notes: "", valid_days: 14, deposit_percent: 25 });
  const load = () => call("get", "/admin/quote-requests").then(setRows);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => { try { await call("post", `/admin/quote-requests/${r.id}/quote`, { ...q, amount: Number(q.amount) }); toast.success("Quote approved & sent to customer"); setR(null); load(); } catch (e) { toast.error(errMsg(e)); } };
  const decline = async () => { await call("patch", `/admin/quote-requests/${r.id}/decline`); setR(null); load(); };
  return (
    <>
      <H title="Custom quotes" />
      <Table testid="quotes-table" rows={rows} onRow={(x) => { setR(x); setQ({ amount: x.quote?.amount || "", notes: x.quote?.notes || "", valid_days: 14, deposit_percent: 25 }); }}
        cols={[["Received", (x) => x.created_at.slice(0, 10)], ["Customer", (x) => x.contact.name], ["Service", (x) => x.service_name], ["ZIP", (x) => x.address.zip], ["Photos", (x) => x.photo_ids.length], ["Quote", (x) => (x.quote ? money(x.quote.amount) : "—")], ["Status", (x) => <StatusBadge s={x.status} />]]} />
      <Dialog open={!!r} onOpenChange={(o) => !o && setR(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-review-dialog">
          {r && <>
            <DialogHeader><DialogTitle>{r.service_name} — {r.contact.name}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1">
              <p>{r.contact.email} · {r.contact.phone}</p>
              <p>{r.address.line1}, {r.address.city} {r.address.zip}</p>
              <p>{r.property.property_type} · {r.property.bedrooms} bd · {r.property.bathrooms} ba · {r.property.sqft} sqft {r.preferred_date && `· preferred ${r.preferred_date}`}</p>
              <p className="whitespace-pre-line bg-slate-50 rounded-lg p-3 mt-2">{r.description}</p>
            </div>
            {r.photo_ids.length > 0 && <div className="grid grid-cols-3 gap-2" data-testid="quote-photos">{r.photo_ids.map((id) => <Photo key={id} id={id} />)}</div>}
            {["new", "quoted", "accepted"].includes(r.status) && (
              <div className="grid sm:grid-cols-3 gap-3 border-t pt-4">
                <div><label className="label">Price $</label><input type="number" className="field" value={q.amount} onChange={(e) => setQ({ ...q, amount: e.target.value })} data-testid="quote-amount-input" /></div>
                <div><label className="label">Valid (days)</label><input type="number" className="field" value={q.valid_days} onChange={(e) => setQ({ ...q, valid_days: Number(e.target.value) })} /></div>
                <div><label className="label">Deposit %</label><input type="number" className="field" value={q.deposit_percent} onChange={(e) => setQ({ ...q, deposit_percent: Number(e.target.value) })} /></div>
                <textarea className="field sm:col-span-3" rows={3} placeholder="Scope / notes for the customer" value={q.notes} onChange={(e) => setQ({ ...q, notes: e.target.value })} data-testid="quote-notes-input" />
                <div className="sm:col-span-3 flex gap-2"><button className="btn-primary" onClick={send} data-testid="quote-send-button">Approve & send quote</button><button className="btn-outline" onClick={decline} data-testid="quote-decline-button">Decline</button></div>
              </div>
            )}
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function Payments() {
  const { call } = useAdmin();
  const [rows, setRows] = useState([]);
  const load = () => call("get", "/admin/payments").then(setRows);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refund = async (p) => {
    const max = p.amount - (p.refunded_amount || 0);
    const v = window.prompt(`Refund amount (max ${max.toFixed(2)})`, max.toFixed(2));
    if (!v) return;
    try { await call("post", `/admin/payments/${p.id}/refund`, { amount: Number(v) }); toast.success("Refund issued"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <H title="Payments" />
      <Table testid="payments-table" rows={rows} cols={[["Date", (p) => (p.paid_at || p.created_at).slice(0, 16).replace("T", " ")], ["Booking", (p) => p.booking?.number], ["Customer", (p) => p.booking?.contact?.name], ["Kind", (p) => p.kind], ["Method", (p) => `${p.method || "card"}${p.reference ? ` · ${p.reference}` : ""}`],
        ["Amount", (p) => money(p.amount)], ["Refunded", (p) => money(p.refunded_amount)], ["Status", (p) => <StatusBadge s={p.status} />],
        ["", (p) => <span className="flex gap-2">{p.receipt_url && <a className="text-sky-700" href={p.receipt_url} target="_blank" rel="noreferrer">Receipt</a>}{["paid", "partially_refunded"].includes(p.status) && <button className="text-red-600 font-medium" onClick={() => refund(p)} data-testid={`refund-button-${p.id}`}>Refund</button>}</span>]]} />
    </>
  );
}

export function Reports() {
  const { call } = useAdmin();
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: today.slice(0, 8) + "01", end: today });
  const [d, setD] = useState(null);
  useEffect(() => { call("get", `/admin/reports?start=${range.start}&end=${range.end}`).then(setD); }, [range, call]);
  return (
    <>
      <H title="Revenue reports">
        <input type="date" className="field !py-2" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="reports-start" />
        <input type="date" className="field !py-2" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="reports-end" />
      </H>
      {d && <>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="Gross" value={money(d.gross)} testid="reports-gross" /><Stat label="Refunds" value={money(d.refunds)} /><Stat label="Net revenue" value={money(d.net)} testid="reports-net" /><Stat label="Payments" value={d.payments} /><Stat label="Bookings" value={d.bookings} />
        </div>
        <div className="card h-80"><ResponsiveContainer><BarChart data={d.by_day}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="amount" fill="#0284C7" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
        <Table testid="reports-by-service" rows={d.by_service} cols={[["Service", (r) => r.service], ["Net revenue", (r) => money(r.amount)]]} />
      </>}
    </>
  );
}

export function Audit() {
  const { call } = useAdmin();
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState([]);
  useEffect(() => { call("get", "/admin/audit").then(setRows); call("get", "/admin/notifications").then(setNotes); }, [call]);
  return (
    <>
      <H title="Audit log" />
      <Table testid="audit-table" rows={rows} cols={[["When", (r) => r.created_at.slice(0, 19).replace("T", " ")], ["Who", (r) => r.actor], ["Action", (r) => r.action], ["Entity", (r) => `${r.entity} ${r.entity_id.slice(0, 8)}`], ["Details", (r) => <span className="text-xs text-slate-500 truncate max-w-xs inline-block">{JSON.stringify(r.details)}</span>]]} />
      <h2 className="text-lg font-semibold">Notification log</h2>
      <Table testid="notifications-table" rows={notes} cols={[["When", (r) => r.created_at.slice(0, 19).replace("T", " ")], ["Channel", (r) => r.channel], ["Event", (r) => r.event], ["To", (r) => r.to], ["Status", (r) => <StatusBadge s={r.status} />], ["Error", (r) => <span className="text-xs text-red-500">{r.error}</span>]]} />
    </>
  );
}

export function Users() {
  const { call, me } = useAdmin();
  const [d, setD] = useState(null);
  const load = () => call("get", "/users").then(setD);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setRole = async (u, role) => { try { await call("patch", `/users/${u.user_id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <>
      <H title="Staff access" />
      <p className="text-sm text-slate-500">Anyone can create an account at {window.location.origin}/sign-up. New accounts are customers (they see only their own bookings). Give staff a role here — Admin: full access including prices, schedule and payments · Manager: bookings, customers, employees, quotes · Cleaner: assigned jobs only.</p>
      <Table testid="users-table" rows={d.users} cols={[["Name", (u) => u.name], ["Email", (u) => u.email], ["Role", (u) => (
        <select className="field !py-1.5 w-36" value={u.role} disabled={u.user_id === me.user_id} onChange={(e) => setRole(u, e.target.value)} data-testid={`user-role-select-${u.email}`}>{d.roles.map((r) => <option key={r}>{r}</option>)}</select>)]]} />
    </>
  );
}

export function Messages() {
  const { call } = useAdmin();
  const [rows, setRows] = useState([]);
  useEffect(() => { call("get", "/admin/messages").then(setRows); }, [call]);
  return (<><H title="Website messages" /><Table testid="messages-table" rows={rows} cols={[["When", (r) => r.created_at.slice(0, 16).replace("T", " ")], ["Name", (r) => r.name], ["Email", (r) => r.email], ["Phone", (r) => r.phone], ["Message", (r) => <span className="whitespace-normal block max-w-md">{r.message}</span>]]} /></>);
}

export function MyJobs() {
  const { call } = useAdmin();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const load = () => call("get", "/staff/my-jobs").then(setRows).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = async (j, status) => { try { await call("patch", `/staff/jobs/${j.id}/status`, { status }); toast.success("Job updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (err) return <><H title="My jobs" /><p className="text-slate-600" data-testid="my-jobs-error">{err}</p></>;
  if (!rows) return null;
  return (
    <>
      <H title="My jobs" />
      {rows.length === 0 && <p className="text-slate-500">No assigned jobs.</p>}
      <div className="grid gap-4 md:grid-cols-2" data-testid="my-jobs-list">
        {rows.map((j) => (
          <div key={j.id} className="card space-y-2 text-sm" data-testid={`job-card-${j.number}`}>
            <div className="flex justify-between"><b>{j.date} · {j.time}</b><StatusBadge s={j.status} /></div>
            <p className="font-semibold text-sky-800">{j.price.service_name_en}</p>
            <p>{j.contact.name} · <a href={`tel:${j.contact.phone}`} className="text-sky-700">{j.contact.phone}</a></p>
            <p>{j.address.line1} {j.address.line2}, {j.address.city} {j.address.zip}</p>
            {j.address.notes && <p className="text-slate-500">{j.address.notes}</p>}
            <p className="text-slate-500">{j.property.bedrooms} bd · {j.property.bathrooms} ba · {j.property.sqft} sqft {j.price.addons.map((a) => `· ${a.name_en}`).join(" ")}</p>
            <div className="flex gap-2 pt-2">
              {["confirmed", "assigned"].includes(j.status) && <button className="btn-dark !py-2" onClick={() => upd(j, "in_progress")} data-testid="cleaner-status-update-button">Start job</button>}
              {j.status === "in_progress" && <button className="btn-primary !py-2" onClick={() => upd(j, "completed")} data-testid="cleaner-complete-button">Mark completed</button>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export { API };
